import { useTranslation } from "react-i18next";
import { Box, Typography, Skeleton } from "@mui/material";

import { JobSummary as JobSummaryType } from "../../../../types/job";

const jobSummaryCardBG = {
  allCandidates: "linear-gradient(292deg, rgba(44, 77, 237, 0.20) -132.72%, rgba(255, 255, 255, 0.20) 74.08%), #FFF",
  shortListed: "linear-gradient(292deg, rgba(157, 0, 236, 0.20) -132.72%, rgba(255, 255, 255, 0.20) 74.08%), #FFF",
  qualified: "linear-gradient(292deg, rgba(3, 145, 3, 0.20) -132.72%, rgba(255, 255, 255, 0.20) 74.08%), #FFF",
  disregarded: "linear-gradient(292deg, rgba(255, 77, 78, 0.20) -58.2%, rgba(255, 255, 255, 0.20) 74.11%), #FFF",
};

const JobSummary = ({
  jobSummary,
  isJobSummaryLoading,
}: {
  jobSummary?: JobSummaryType;
  isJobSummaryLoading: boolean;
}) => {
  const { t } = useTranslation("goHire", { keyPrefix: "jobDetailsPage.overViewTab" });

  const jobSummaryData = [
    {
      number: jobSummary?.allCandidates,
      title: "allCandidates",
    },
    {
      number: jobSummary?.shortListed,
      title: "shortListed",
    },
    {
      number: jobSummary?.qualified,
      title: "qualified",
    },
    {
      number: jobSummary?.disregarded,
      title: "disregarded",
    },
  ];

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("jobSummary")}</Typography>
      <Box sx={styles.summaryCardsContainer}>
        {isJobSummaryLoading &&
          Array.from({ length: 4 }).map((_, index) => (
            <Box key={index} sx={styles.skeletonCardContainer}>
              <Skeleton variant="rectangular" width="40px" height="30px" sx={{ borderRadius: "4px" }} />
              <Skeleton variant="text" width="80px" height="20px" />
            </Box>
          ))}

        {!isJobSummaryLoading &&
          jobSummaryData.map(({ number, title }) => (
            <Box key={title} sx={styles.summaryCardContainer(title)}>
              <Typography sx={styles.summaryCardValue}>{number ?? 0}</Typography>
              <Typography sx={styles.summaryCardTitle}>{t(title)}</Typography>
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export default JobSummary;

const styles = {
  container: {
    background: "#FFF",
    padding: "24px 24px 0",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  title: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  summaryCardsContainer: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: "16px",
  },
  summaryCardContainer: (title: string) => ({
    flex: 1,
    background: jobSummaryCardBG[title as keyof typeof jobSummaryCardBG],
    display: "flex",
    padding: "16px",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "6px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
  }),
  skeletonCardContainer: {
    flex: 1,
    background: "#FFF",
    display: "flex",
    padding: "16px",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: "6px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
  },
  summaryCardValue: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "30px",
  },
  summaryCardTitle: {
    color: "var(--Dark---600, #707070)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
};
